export type DomParserPlugin = (document: Document) => Document

const PLUGIN_MAP = {
  mathml: sanitizeMathMl,
  tts: sanitizeTTS,
  remove_title: removeFirstH1,
} as const satisfies Record<string, DomParserPlugin>

export type DomParserPluginKey = keyof typeof PLUGIN_MAP

export type DomParser = (input: string | null | undefined, plugins?: Array<DomParserPluginKey | DomParserPlugin>) => Document

/**
 * Plugin that provides a function to parse a string into an HTML document.
 *
 * Uses the built-in `DOMParser` API on the client side and `jsdom` on the server side to reduce bundle size.
 */
export default defineNuxtPlugin({
  name: "dom-parser",
  async setup() {
    let parse: (input: string) => Document

    const site = useSiteConfig()

    if (import.meta.client) {
      const parser = new DOMParser()

      parse = input => parser.parseFromString(input, "text/html")
    } else {
      // jsdom is quite big, so we only import it on the server
      const { JSDOM } = await import("jsdom")

      parse = (input) => {
        const { window } = new JSDOM(input, {
          url: site.url,
          referrer: site.url,
          contentType: "text/html",
          includeNodeLocations: true,
        })

        return window.document
      }
    }

    const parseStringToDocument: DomParser = (input, plugins = []) => plugins.reduce(
      (acc, plugin) => typeof plugin === "function" ? plugin(acc) : PLUGIN_MAP[plugin](acc),
      parse(input ?? ""),
    )

    return {
      provide: {
        parseStringToDocument,
      },
    }
  },
})
